import { AxiosError } from "axios";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "@/configs/router.config/routes";
import { AgentMessage } from "@/models/agent/message";
import { AgentService } from "@/services/agent/agent.service";
import { useAgentStore } from "@/stores/agent/useAgentStore";
import { useWorkspaces } from "../useWorkspaces";

const PAGE_SIZE = 40;

interface UseThreadMessagesResult {
  messages: AgentMessage[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  observerRef: (node: HTMLDivElement | null) => void;
  silentRefresh: () => Promise<void>;
}

export const useThreadMessages = (
  threadId?: string,
  scrollRef?: RefObject<HTMLDivElement>,
): UseThreadMessagesResult => {
  const navigate = useNavigate();
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const messages = useAgentStore((state) => state.messages);
  const addMessages = useAgentStore((state) => state.addMessages);
  const resetMessages = useAgentStore((state) => state.resetMessages);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const observer = useRef<IntersectionObserver>();
  const currentThreadIdRef = useRef(threadId);

  const fetchMessages = useCallback(async (threadId: string, offset: number) => {
    try {
      const [{ data }] = await Promise.all([
        AgentService.getThreadMessages(threadId, offset, PAGE_SIZE),
        new Promise<void>(r => setTimeout(r, 750)),
      ]);
      if (!data) return;
      if (currentThreadIdRef.current != threadId) return;

      // サブエージェントの最終出力は表示しない
      addMessages(data.filter((msg) => !msg.isSubAgentFinalResponse));
      setHasMore(data?.length === PAGE_SIZE); // 返された件数がPAGE_SIZEより少ない場合は終了
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 404) {
        return navigate(routes.agent(sessionCurrentWorkspaceId));
      }
      console.error("Error fetching messages:", e);
    } finally {
      setIsLoading(false);
    }
  }, [addMessages, navigate, sessionCurrentWorkspaceId]);

  // スレッドが変更された場合のリセット処理
  useEffect(() => {
    currentThreadIdRef.current = threadId;

    resetMessages();
    setHasMore(true);
    setIsLoading(true);
    setIsLoadingMore(false);
    setIsInitialized(false);
  }, [threadId, resetMessages]);

  // 初回データの処理
  useEffect(() => {
    if (threadId && !isInitialized) {
      setIsLoading(true);
      fetchMessages(threadId, 0);
      setIsInitialized(true);
    }
  }, [threadId, isInitialized, fetchMessages, addMessages]);

  // 追加データをロードする関数（過去のメッセージ）
  const loadMoreData = useCallback(async () => {
    if (!threadId || isLoadingMore || !hasMore) return;

    try {
      await fetchMessages(threadId, messages.length + 1);
    } catch (err) {
      console.error("Error loading more messages:", err);
    }
  }, [threadId, isLoadingMore, hasMore, messages.length, fetchMessages]);

  // サイレントリフレッシュ（既存メッセージを保持しながら新しいメッセージを取得）
  const silentRefresh = useCallback(async () => {
    if (!threadId) return;

    try {
      resetMessages();
      await fetchMessages(threadId, 0);
    } catch (error) {
      console.error("[silentRefresh] Error:", error);
    }
  }, [threadId, resetMessages, fetchMessages]);

  // Intersection Observerのコールバック参照（スクロール最上部を監視）
  const observerRef = useCallback((node: HTMLDivElement | null) => {
    if (!isInitialized || isLoadingMore || !threadId) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(async entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        const el = scrollRef?.current;
        const prev = el?.scrollHeight;

        setIsLoadingMore(true);
        await loadMoreData();

        requestAnimationFrame(() => {
          if (prev && el) {
            el.scrollTop += el.scrollHeight - prev;
          }
          setIsLoadingMore(false);
        });
      }
    }, {
      root: scrollRef?.current,
      threshold: 0,
      rootMargin: "40px 0px 0px 0px",
    });

    if (node) {
      observer.current.observe(node);
    }
  }, [isLoadingMore, hasMore, loadMoreData, threadId, isInitialized, scrollRef]);

  return {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    observerRef,
    silentRefresh,
  };
};
